// react
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import moment from "moment";
import Datetime from "react-datetime";
import Select from 'react-select';
import SweetAlert from "react-bootstrap-sweetalert";

// tiptap
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import { FontFamily } from '@tiptap/extension-font-family';
import FontSize from "beaktShared/components/TipTap/FontSize";
import MenuBar from "beaktShared/components/TipTap/MenuBar";
import "assets/css/tiptap.css";

// @material-ui/icons
import Announcement from "@material-ui/icons/Announcement";
import AttachFile from "@material-ui/icons/AttachFile";

// @material-ui components
import TextField from '@material-ui/core/TextField';
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";

// core components
import GridContainer from "beaktShared/components/Grid/GridContainer.js";
import GridItem from "beaktShared/components/Grid/GridItem.js";
import Button from "beaktShared/components/CustomButtons/Button.js";
import Card from "beaktShared/components/Card/Card.js";
import CardBody from "beaktShared/components/Card/CardBody.js";
import CardIcon from "beaktShared/components/Card/CardIcon.js";
import CardHeader from "beaktShared/components/Card/CardHeader.js";
import Snackbar from "beaktShared/components/Snackbar/Snackbar.js";
import SnackbarContent from "beaktShared/components/Snackbar/SnackbarContent.js";

// services
import * as ComunicadoService from "./ComunicadoService";
import { formateDate, getLocale, getStringFormatDate } from "beaktShared/helpers/masks";
import { ValidSelect } from "beaktShared/helpers/validselect";
import { SessionContext } from "beaktShared/services/session";

// styles
import { makeStyles } from "@material-ui/core/styles";
import styles from "assets/jss/material-dashboard-pro-react/views/regularFormsStyle";
import buttonStyle from "assets/jss/material-dashboard-pro-react/components/buttonStyle.js";

const { TipoComunicado, SituacaoComunicado, TipoEscopoComunicado } = ComunicadoService;

const useStyles = makeStyles({
    ...buttonStyle,
    ...styles,
    editor: {
        minHeight: "260px",
        border: "1px solid #999999",
        borderRadius: "5px",
        padding: "10px",
        overflowY: "auto",
        "& .ProseMirror": {
            minHeight: "200px",
            outline: "none"
        }
    },
    editorError: {
        borderColor: "#f44336"
    },
    anexos: {
        marginTop: "8px",
        "& > *": {
            margin: "0 8px 8px 0"
        }
    },
    linkAcao: {
        display: "inline-block",
        marginTop: "10px"
    }
});

// Regras espelhadas de api/Services/Matriz/ComunicadoService.cs
const TAMANHO_MAXIMO_TITULO = 200;
const EXTENSOES_PERMITIDAS = [".pdf", ".png", ".jpg", ".jpeg", ".docx", ".xlsx"];
const TAMANHO_MAXIMO_ANEXO_BYTES = 10 * 1024 * 1024;
// Protecao do cliente: o base64 aumenta ~33% o payload e o limite padrao de requisicao do servidor e ~28-30 MB
const TAMANHO_MAXIMO_ENVIO_BYTES = 20 * 1024 * 1024;

const FORMATO_DATA_API = "YYYY-MM-DD";
const HORA_LIMITE_ACEITE = "T23:59:59";

const formatarTamanho = (bytes) => {
    if (!bytes) return "0 KB";
    if (bytes < 1024 * 1024) return Math.max(1, Math.round(bytes / 1024)) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1).replace(".", ",") + " MB";
};

const obterExtensao = (nomeArquivo) => {
    const indice = nomeArquivo.lastIndexOf(".");
    return indice >= 0 ? nomeArquivo.substring(indice).toLowerCase() : "";
};

// O servidor espera o base64 puro (Convert.FromBase64String), sem o prefixo "data:...;base64,"
const lerArquivoBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
});

// Props: item (linha da listagem ou {} para novo), addItem(mensagem) apos salvar, handleView() para voltar
export default function ComunicadoForm({ item, addItem, handleView }) {
    const { t } = useTranslation();
    const classes = useStyles();
    const session = useContext(SessionContext);
    const inputAnexoRef = useRef(null);

    const comunicadoId = item && item.comunicadoId ? item.comunicadoId : 0;
    const isEdicao = comunicadoId > 0;

    const tipoOptions = [
        { value: TipoComunicado.AvisoSistema, label: t('notice_board.type_system_notice') },
        { value: TipoComunicado.InformativoUnidade, label: t('notice_board.type_unit_information') },
        { value: TipoComunicado.Alerta, label: t('notice_board.type_alert') }
    ];

    const escopoOptions = [
        { value: TipoEscopoComunicado.Matriz, label: t('notice_board.scope_matriz') },
        { value: TipoEscopoComunicado.Estabelecimento, label: t('notice_board.scope_establishment') }
    ];

    const [notification, setNotification] = useState({ show: false, color: 'info', message: '' });
    const showNotification = (message, color) => {
        setNotification({ show: true, color: color, message: message });
        setTimeout(() => setNotification({ show: false }), 6000);
    };

    // ************************** CAMPOS **************************
    const [titulo, setTitulo] = useState("");
    const [tipo, setTipo] = useState(null);
    const [tipoEscopo, setTipoEscopo] = useState(null);
    const [estabelecimentoIds, setEstabelecimentoIds] = useState([]);
    const [dataLimite, setDataLimite] = useState("");
    const [dataLimiteOriginal, setDataLimiteOriginal] = useState(null);
    const [anexos, setAnexos] = useState([]);
    const [situacao, setSituacao] = useState(SituacaoComunicado.Pendente);
    const [descricaoInicial, setDescricaoInicial] = useState(null);
    const [errors, setErrors] = useState({});

    const isPublicado = situacao === SituacaoComunicado.Ativo;

    const editor = useEditor({
        extensions: [
            StarterKit,
            TextStyle,
            FontFamily,
            FontSize,
            TextAlign.configure({ types: ['heading', 'paragraph'] })
        ],
        content: ""
    });

    // O editor e criado de forma assincrona: aplica a descricao carregada assim que ambos estiverem prontos
    useEffect(() => {
        if (editor && descricaoInicial !== null)
            editor.commands.setContent(descricaoInicial);
    }, [editor, descricaoInicial]);

    // Comunicado publicado: conteudo e destinatarios congelados (o servidor recusa a alteracao); so o prazo e editavel
    useEffect(() => {
        if (editor)
            editor.setEditable(!isPublicado);
    }, [editor, isPublicado]);

    // ************************** CARGA DO DETALHE (EDICAO) **************************
    const [isLoadingDetalhe, setIsLoadingDetalhe] = useState(isEdicao);
    const [isErrorDetalhe, setIsErrorDetalhe] = useState(false);

    const carregarDetalhe = () => {
        setIsLoadingDetalhe(true);
        setIsErrorDetalhe(false);
        ComunicadoService.obter(comunicadoId).then(result => {
            setIsLoadingDetalhe(false);
            if (!result.ok || !result.data) {
                setIsErrorDetalhe(true);
                if (result.message)
                    showNotification(result.message, 'danger');
                return;
            }
            const detalhe = result.data;
            setTitulo(detalhe.titulo || "");
            setTipo(detalhe.tipo);
            setTipoEscopo(detalhe.tipoEscopo);
            setEstabelecimentoIds((detalhe.estabelecimentos || []).map(e => e.estabelecimentoId));
            setDataLimite(detalhe.dataLimiteAceite ? moment(detalhe.dataLimiteAceite) : "");
            setDataLimiteOriginal(detalhe.dataLimiteAceite || null);
            setSituacao(detalhe.situacao);
            setAnexos((detalhe.anexos || []).map(a => ({
                key: "anexo-" + a.comunicadoAnexoId,
                comunicadoAnexoId: a.comunicadoAnexoId,
                nomeArquivo: a.nomeArquivo,
                tamanhoBytes: a.tamanhoBytes,
                url: a.url,
                arquivo: null
            })));
            setDescricaoInicial(detalhe.descricao || "");
        });
    };

    useEffect(() => {
        if (isEdicao)
            carregarDetalhe();
    // eslint-disable-next-line
    }, [comunicadoId]);

    // ************************** UNIDADES ATIVAS DA FRANQUIA **************************
    const [listaEstabelecimentos, setListaEstabelecimentos] = useState([]);
    const [isLoadingEstabelecimentos, setIsLoadingEstabelecimentos] = useState(false);
    const [estabelecimentosCarregados, setEstabelecimentosCarregados] = useState(false);

    useEffect(() => {
        if (tipoEscopo !== TipoEscopoComunicado.Estabelecimento || estabelecimentosCarregados)
            return;

        setIsLoadingEstabelecimentos(true);
        ComunicadoService.listarEstabelecimentosAtivos(session.franquiaId).then(result => {
            setIsLoadingEstabelecimentos(false);
            if (!result.ok) {
                if (result.message)
                    showNotification(result.message, 'danger');
                return;
            }
            setListaEstabelecimentos(result.data || []);
            setEstabelecimentosCarregados(true);
        });
    // eslint-disable-next-line
    }, [tipoEscopo]);

    const handleEscopoChange = (opt) => {
        const valor = opt ? opt.value : null;
        setTipoEscopo(valor);
        // Escopo Matriz nao aceita unidades destinatarias
        if (valor !== TipoEscopoComunicado.Estabelecimento)
            setEstabelecimentoIds([]);
    };

    const handleSelecionarTodosEstabelecimentos = () => {
        setEstabelecimentoIds(listaEstabelecimentos.map(est => est.estabelecimentoId));
    };

    // ************************** ANEXOS **************************
    const handleSelecionarAnexos = async (e) => {
        const arquivos = Array.from(e.target.files || []);
        e.target.value = "";
        if (arquivos.length === 0)
            return;

        let totalNovos = anexos.filter(a => !a.comunicadoAnexoId).reduce((soma, a) => soma + a.tamanhoBytes, 0);
        const novos = [];

        for (const file of arquivos) {
            if (EXTENSOES_PERMITIDAS.indexOf(obterExtensao(file.name)) === -1) {
                showNotification(t('notice_board.attachment_invalid_extension', { file: file.name, extensions: EXTENSOES_PERMITIDAS.join(", ") }), 'danger');
                continue;
            }
            if (file.size === 0 || file.size > TAMANHO_MAXIMO_ANEXO_BYTES) {
                showNotification(t('notice_board.attachment_invalid_size', { file: file.name }), 'danger');
                continue;
            }
            if (totalNovos + file.size > TAMANHO_MAXIMO_ENVIO_BYTES) {
                showNotification(t('notice_board.attachment_total_exceeded'), 'danger');
                break;
            }

            try {
                const base64 = await lerArquivoBase64(file);
                totalNovos += file.size;
                novos.push({
                    key: "novo-" + Date.now() + "-" + novos.length + "-" + file.name,
                    comunicadoAnexoId: 0,
                    nomeArquivo: file.name,
                    tamanhoBytes: file.size,
                    url: null,
                    arquivo: base64
                });
            } catch {
                showNotification(t('notice_board.attachment_read_error', { file: file.name }), 'danger');
            }
        }

        if (novos.length > 0)
            setAnexos(atual => [...atual, ...novos]);
    };

    const handleRemoverAnexo = (key) => {
        setAnexos(atual => atual.filter(a => a.key !== key));
    };

    // ************************** VALIDACAO E ENVIO **************************
    const isDataValida = (data) => moment.isMoment(data) && data.isValid();

    const montarDataLimite = () => {
        if (!isDataValida(dataLimite))
            return null;
        // Mantem o valor gravado quando o dia nao foi alterado (o servidor so exige data futura quando ela muda)
        if (dataLimiteOriginal && moment(dataLimiteOriginal).isSame(dataLimite, 'day'))
            return dataLimiteOriginal;
        return dataLimite.format(FORMATO_DATA_API) + HORA_LIMITE_ACEITE;
    };

    const validar = (publicar) => {
        const erros = {};
        const tituloLimpo = titulo.trim();

        if (!tituloLimpo)
            erros.titulo = t('label.required');
        else if (tituloLimpo.length > TAMANHO_MAXIMO_TITULO)
            erros.titulo = t('notice_board.title_max_length', { max: TAMANHO_MAXIMO_TITULO });

        if (!editor || editor.state.doc.textContent.trim() === "")
            erros.descricao = t('label.required');

        if (!tipo)
            erros.tipo = t('label.required');

        if (!tipoEscopo)
            erros.tipoEscopo = t('label.required');

        if (dataLimite !== "" && !isDataValida(dataLimite))
            erros.dataLimite = t('error_message.invalid_date');
        else if (publicar && isDataValida(dataLimite)) {
            const dataEnviada = montarDataLimite();
            const alterada = dataEnviada !== dataLimiteOriginal;
            if (alterada && !moment(dataEnviada).isAfter(moment()))
                erros.dataLimite = t('notice_board.deadline_must_be_future');
        }

        setErrors(erros);
        return Object.keys(erros).length === 0;
    };

    const [salvando, setSalvando] = useState(false);
    const [showConfirmPublicar, setShowConfirmPublicar] = useState(false);

    const salvar = (publicar) => {
        if (!validar(publicar)) {
            showNotification(t('notice_board.fix_form_errors'), 'danger');
            return;
        }

        const dto = {
            franquiaId: session.franquiaId,
            titulo: titulo.trim(),
            // Publicado reenvia o HTML gravado: o editor pode re-serializar o conteudo e o servidor entenderia como alteracao
            descricao: isPublicado ? descricaoInicial : editor.getHTML(),
            tipo: tipo,
            dataLimiteAceite: montarDataLimite(),
            tipoEscopo: tipoEscopo,
            estabelecimentoIds: tipoEscopo === TipoEscopoComunicado.Estabelecimento ? estabelecimentoIds : [],
            publicar: publicar,
            anexos: anexos.map(a => a.comunicadoAnexoId
                ? { comunicadoAnexoId: a.comunicadoAnexoId, nomeArquivo: a.nomeArquivo }
                : { comunicadoAnexoId: 0, nomeArquivo: a.nomeArquivo, arquivo: a.arquivo })
        };

        setSalvando(true);
        const requisicao = isEdicao
            ? ComunicadoService.editar(comunicadoId, dto)
            : ComunicadoService.criar(dto);

        requisicao.then(result => {
            setSalvando(false);
            if (!result.ok) {
                if (result.message)
                    showNotification(result.message, 'danger');
                return;
            }

            let mensagem = t('notice_board.draft_saved');
            if (isPublicado)
                mensagem = t('information_message.updated_successfully');
            else if (publicar)
                mensagem = t('notice_board.published_successfully');
            addItem(mensagem);
        });
    };

    const handlePublicar = () => {
        if (!validar(true)) {
            showNotification(t('notice_board.fix_form_errors'), 'danger');
            return;
        }
        setShowConfirmPublicar(true);
    };

    // ************************** RENDER **************************
    const renderConteudo = () => {
        if (isLoadingDetalhe) {
            return (
                <div className={classes.textCenter}>
                    <CircularProgress />
                    <p>{t('label.loading')}...</p>
                </div>
            );
        }

        if (isErrorDetalhe) {
            return (
                <div className={classes.textCenter}>
                    <p>{t('notice_board.load_detail_error')}</p>
                    <Button color="primary" onClick={carregarDetalhe}>{t('notice_board.try_again')}</Button>
                </div>
            );
        }

        return (
            <GridContainer>
                {isPublicado &&
                    <GridItem xs={12}>
                        <SnackbarContent color="info" message={t('notice_board.published_locked_warning')} />
                    </GridItem>}

                <GridItem xs={12}>
                    <FormControl fullWidth className={classes.formControl}>
                        <TextField
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            label={t('label.title') + " *"}
                            name="titulo"
                            disabled={isPublicado}
                            value={titulo}
                            onChange={e => setTitulo(e.target.value)}
                            error={errors.titulo ? true : false}
                            inputProps={{ maxLength: TAMANHO_MAXIMO_TITULO }}
                        />
                        {errors.titulo && <FormHelperText error><span>{errors.titulo}</span></FormHelperText>}
                    </FormControl>
                </GridItem>

                <GridItem xs={12}>
                    <FormControl fullWidth className={classes.formControl}>
                        <label className={errors.descricao ? "select__label select__label_error" : "select__label"}>
                            {t('label.description') + " *"}</label>
                        <div className={classes.editor + (errors.descricao ? " " + classes.editorError : "")}>
                            {!isPublicado && <MenuBar editor={editor} />}
                            <EditorContent editor={editor} style={{ width: "100%" }} />
                        </div>
                        {errors.descricao && <FormHelperText error><span>{errors.descricao}</span></FormHelperText>}
                    </FormControl>
                </GridItem>

                <GridItem xs={12} sm={6} md={4}>
                    <FormControl fullWidth className={classes.formControl}>
                        <label className={errors.tipo ? "select__label select__label_error" : "select__label"}>
                            {t('notice_board.notice_type') + " *"}</label>
                        <Select
                            classNamePrefix="select"
                            styles={ValidSelect(errors.tipo ? true : false)}
                            isSearchable={false}
                            isDisabled={isPublicado}
                            name="tipo"
                            placeholder={t('label.select') + "..."}
                            options={tipoOptions}
                            value={tipoOptions.filter(o => o.value === tipo)}
                            onChange={(opt) => setTipo(opt ? opt.value : null)}
                        />
                        {errors.tipo && <FormHelperText error><span>{errors.tipo}</span></FormHelperText>}
                    </FormControl>
                </GridItem>

                <GridItem xs={12} sm={6} md={4}>
                    <FormControl fullWidth className={classes.formControl}>
                        <label className={errors.dataLimite ? "select__label select__label_error" : "select__label"}>
                            {t('notice_board.deadline')}</label>
                        <Datetime
                            locale={getLocale()}
                            dateFormat={getStringFormatDate()}
                            timeFormat={false}
                            value={dataLimite}
                            onChange={date => setDataLimite(date)}
                            inputProps={{ name: "dataLimite", readOnly: 'readonly', placeholder: t('notice_board.no_deadline') }}
                            className={errors.dataLimite ? "error" : ""}
                        />
                        {errors.dataLimite && <FormHelperText error><span>{errors.dataLimite}</span></FormHelperText>}
                        {dataLimite !== "" &&
                            <Button link color="primary" size="sm" className={classes.linkAcao}
                                onClick={() => setDataLimite("")}>
                                {t('notice_board.remove_deadline')}
                            </Button>}
                    </FormControl>
                </GridItem>

                <GridItem xs={12} sm={12} md={4}>
                    <FormControl fullWidth className={classes.formControl}>
                        <label className={errors.tipoEscopo ? "select__label select__label_error" : "select__label"}>
                            {t('notice_board.recipients') + " *"}</label>
                        <Select
                            classNamePrefix="select"
                            styles={ValidSelect(errors.tipoEscopo ? true : false)}
                            isSearchable={false}
                            isDisabled={isPublicado}
                            name="tipoEscopo"
                            placeholder={t('label.select') + "..."}
                            options={escopoOptions}
                            value={escopoOptions.filter(o => o.value === tipoEscopo)}
                            onChange={handleEscopoChange}
                        />
                        {errors.tipoEscopo && <FormHelperText error><span>{errors.tipoEscopo}</span></FormHelperText>}
                    </FormControl>
                </GridItem>

                {tipoEscopo === TipoEscopoComunicado.Estabelecimento &&
                    <GridItem xs={12}>
                        <FormControl fullWidth className={classes.formControl}>
                            <label className="select__label">{t('label.establishments')}</label>
                            <Select
                                classNamePrefix="select"
                                styles={ValidSelect(false)}
                                isSearchable={true}
                                isMulti={true}
                                isDisabled={isPublicado}
                                name="estabelecimentos"
                                isLoading={isLoadingEstabelecimentos}
                                placeholder={t('notice_board.all_establishments')}
                                noOptionsMessage={() => t('information_message.no_records_found')}
                                options={listaEstabelecimentos}
                                getOptionLabel={({ descricao }) => descricao}
                                getOptionValue={({ estabelecimentoId }) => estabelecimentoId}
                                value={listaEstabelecimentos.filter(({ estabelecimentoId }) => estabelecimentoIds.indexOf(estabelecimentoId) !== -1)}
                                onChange={(value) => setEstabelecimentoIds((value || []).map(v => v.estabelecimentoId))}
                            />
                            <FormHelperText>{t('notice_board.establishments_hint')}</FormHelperText>
                            {listaEstabelecimentos.length > 0 && !isPublicado &&
                                <Button link color="primary" size="sm" className={classes.linkAcao} onClick={handleSelecionarTodosEstabelecimentos}>
                                    {t('label.select_all_establishments')}
                                </Button>}
                        </FormControl>
                    </GridItem>}

                <GridItem xs={12}>
                    <FormControl fullWidth className={classes.formControl}>
                        <label className="select__label">{t('notice_board.attachments')}</label>
                        {!isPublicado && <div>
                            <input
                                ref={inputAnexoRef}
                                type="file"
                                multiple
                                accept={EXTENSOES_PERMITIDAS.join(",")}
                                style={{ display: "none" }}
                                onChange={handleSelecionarAnexos}
                            />
                            <Button size="sm" color="info" round onClick={() => inputAnexoRef.current && inputAnexoRef.current.click()}>
                                <AttachFile /> {t('notice_board.add_attachments')}
                            </Button>
                        </div>}
                        {!isPublicado &&
                            <FormHelperText>{t('notice_board.attachments_hint', { extensions: EXTENSOES_PERMITIDAS.join(", ") })}</FormHelperText>}
                        {anexos.length > 0 &&
                            <div className={classes.anexos}>
                                {anexos.map(anexo => (
                                    <Chip
                                        key={anexo.key}
                                        variant="outlined"
                                        color="primary"
                                        icon={<AttachFile />}
                                        label={anexo.nomeArquivo + " · " + formatarTamanho(anexo.tamanhoBytes)}
                                        onDelete={isPublicado ? undefined : () => handleRemoverAnexo(anexo.key)}
                                        clickable={anexo.url ? true : false}
                                        onClick={anexo.url ? () => window.open(anexo.url, "_blank", "noopener,noreferrer") : undefined}
                                    />
                                ))}
                            </div>}
                    </FormControl>
                </GridItem>

                <GridItem xs={12}>
                    <SnackbarContent
                        color="warning"
                        message={
                            <span>
                                {t('notice_board.mandatory_acceptance_warning')}
                                {isDataValida(dataLimite) &&
                                    <> {t('notice_board.deadline_inactivation_warning', { date: formateDate(dataLimite) })}</>}
                            </span>
                        }
                    />
                </GridItem>
            </GridContainer>
        );
    };

    const podeSalvar = !isLoadingDetalhe && !isErrorDetalhe && !salvando;

    return (
        <div>
            <GridContainer>
                <GridItem xs={12}>
                    <Card>
                        <CardHeader color="primary" icon>
                            <CardIcon color="primary">
                                <Announcement />
                            </CardIcon>
                            <h4 className={classes.cardIconTitle}>
                                {isEdicao ? t('notice_board.edit_notice') : t('notice_board.new_notice')}
                            </h4>
                        </CardHeader>
                        <CardBody>
                            {renderConteudo()}
                        </CardBody>
                    </Card>
                </GridItem>
            </GridContainer>

            <GridContainer justify="center">
                <GridItem xs={12} className={classes.textCenter}>
                    <Button color="transparent" disabled={salvando} onClick={() => handleView()}>
                        {t('button.cancel')}
                    </Button>
                    {!isPublicado &&
                        <Button disabled={!podeSalvar} onClick={() => salvar(false)}>
                            {t('notice_board.save_draft')}
                        </Button>}
                    {!isPublicado &&
                        <Button color="primary" disabled={!podeSalvar} onClick={handlePublicar}>
                            {t('notice_board.publish')}
                        </Button>}
                    {isPublicado &&
                        <Button color="primary" disabled={!podeSalvar} onClick={() => salvar(true)}>
                            {t('notice_board.save_changes')}
                        </Button>}
                    {salvando && <div>{t('label.loading')}...</div>}
                </GridItem>
            </GridContainer>

            {showConfirmPublicar &&
                <SweetAlert
                    warning
                    style={{ display: "block", marginTop: "-200px" }}
                    title={t('notice_board.confirm_publish_title')}
                    onConfirm={() => { setShowConfirmPublicar(false); salvar(true); }}
                    onCancel={() => setShowConfirmPublicar(false)}
                    confirmBtnCssClass={classes.button + " " + classes.success}
                    cancelBtnCssClass={classes.button + " " + classes.danger}
                    confirmBtnText={t('notice_board.confirm_publish_button')}
                    cancelBtnText={t('button.cancel')}
                    showCancel
                >
                    {t('notice_board.confirm_publish_message')}
                </SweetAlert>}

            {notification.show &&
                <Snackbar
                    place="tr"
                    color={notification.color}
                    message={notification.message}
                    open={notification.show}
                    closeNotification={() => setNotification({ show: false })}
                    close
                />}
        </div>
    );
}
